import { useMemo } from "react";
import { NO_DATA } from "@/constants";
import { normalizeS3Key } from "@/lib/utils";
import type {
  AgreementFileDownload,
  AgreementFileDownloadTotal,
} from "@/schemas/agreement";

/**
 * Zdarzenia pobrań jednego pliku. `count` i `lastDownloadedAt` biorą się z agregatu backendu
 * (`file_download_totals`), bo lista `file_downloads` w szczegółach jest przycięta do najnowszych
 * zdarzeń umowy; bez agregatu (starszy backend) liczymy z listy. `last` to ostatnie zdarzenie
 * z listy - brak, gdy wszystkie zdarzenia pliku wypadły poza limit listy.
 */
export interface AgreementFileDownloadsSummary {
  count: number;
  last?: AgreementFileDownload;
  lastDownloadedAt: string;
  entries: AgreementFileDownload[];
}

export type AgreementFileDownloadsByPath = ReadonlyMap<
  string,
  AgreementFileDownloadsSummary
>;

/** Klucz grupowania: ścieżka bez wiodącego `/`, bo `agreement_files[].path` i `event.path` mogą się różnić tylko nim. */
export const toFileDownloadsKey = (path: string): string =>
  normalizeS3Key(path);

const byDownloadedAtAscending = (
  a: AgreementFileDownload,
  b: AgreementFileDownload,
): number => Date.parse(a.downloaded_at) - Date.parse(b.downloaded_at);

const laterOf = (a: string, b: string | undefined): string =>
  b !== undefined && Date.parse(b) > Date.parse(a) ? b : a;

/** „Nazwisko Imię (Rola)" do linii pobrań i okna historii; brak roli → `NO_DATA`. */
export const formatFileDownloadUser = (entry: AgreementFileDownload): string =>
  // CONCAT_WS w widoku daje pusty tekst, gdy konto użytkownika zniknęło - stąd `||`, nie `??`.
  `${entry.downloaded_by_name || NO_DATA} (${entry.downloaded_by_role_name || NO_DATA})`;

/**
 * Grupuje zdarzenia pobrań per plik. Backend zwraca listę chronologicznie, ale po
 * `setQueryData` dopisujemy zdarzenia na końcu, więc sortujemy dla pewności (bez mutacji wejścia).
 * Agregat (gdy jest) nadpisuje liczbę i czas ostatniego dostępu oraz dokłada pliki, których
 * zdarzenia w całości wypadły poza przyciętą listę.
 */
export function groupFileDownloads(
  fileDownloads: readonly AgreementFileDownload[] | undefined,
  totals?: readonly AgreementFileDownloadTotal[],
): AgreementFileDownloadsByPath {
  const buckets = new Map<string, AgreementFileDownload[]>();
  for (const entry of fileDownloads ?? []) {
    const key = toFileDownloadsKey(entry.path);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      buckets.set(key, [entry]);
    }
  }

  const grouped = new Map<string, AgreementFileDownloadsSummary>();
  for (const [key, entries] of buckets) {
    const sorted = [...entries].sort(byDownloadedAtAscending);
    const last = sorted[sorted.length - 1];
    grouped.set(key, {
      count: sorted.length,
      last,
      lastDownloadedAt: last.downloaded_at,
      entries: sorted,
    });
  }
  for (const total of totals ?? []) {
    const key = toFileDownloadsKey(total.path);
    const fromList = grouped.get(key);
    grouped.set(key, {
      // Agregat liczy wszystkie zdarzenia; lista nigdy nie powinna mieć więcej - `max` tylko dla odporności.
      count: Math.max(total.total, fromList?.count ?? 0),
      last: fromList?.last,
      lastDownloadedAt: laterOf(
        total.last_downloaded_at,
        fromList?.lastDownloadedAt,
      ),
      entries: fromList?.entries ?? [],
    });
  }
  return grouped;
}

/** Po nowym zdarzeniu z `download-url`: +1 i nowy czas dla pliku albo nowy wiersz agregatu (bez mutacji). */
export const bumpFileDownloadTotal = (
  totals: readonly AgreementFileDownloadTotal[],
  event: AgreementFileDownload,
): AgreementFileDownloadTotal[] => {
  const key = toFileDownloadsKey(event.path);
  const index = totals.findIndex(
    (total) => toFileDownloadsKey(total.path) === key,
  );
  if (index === -1) {
    return [
      ...totals,
      { path: event.path, total: 1, last_downloaded_at: event.downloaded_at },
    ];
  }
  return totals.map((total, position) =>
    position === index
      ? {
          ...total,
          total: total.total + 1,
          last_downloaded_at: event.downloaded_at,
        }
      : total,
  );
};

/**
 * Zdarzenia pobrań z `file_downloads` szczegółów umowy pogrupowane per plik (ASMMNT-83, etap 2).
 *
 * @param fileDownloads lista z `ReadAgreementDetail.file_downloads` (może być `undefined` na starszym backendzie)
 * @param totals agregat `ReadAgreementDetail.file_download_totals` (jw.)
 * @returns mapa `normalizeS3Key(path)` → podsumowanie
 */
export const useAgreementFileDownloads = (
  fileDownloads: readonly AgreementFileDownload[] | undefined,
  totals?: readonly AgreementFileDownloadTotal[],
): AgreementFileDownloadsByPath =>
  useMemo(
    () => groupFileDownloads(fileDownloads, totals),
    [fileDownloads, totals],
  );
